import {
  createContext,
  PropsWithChildren,
  useCallback,
  use,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  signInWithPopup,
  updateProfile,
  UserCredential,
} from "firebase/auth";
import { doc, setDoc, updateDoc } from "firebase/firestore";

import { useTranslation } from "react-i18next";
import { auth } from "~/integrations/firebase/auth";

import { getUsersCollection, IUser } from "~/integrations/firebase/collections";
import { GlobalLoading } from "~/components/loading";
import { ErrorPage } from "~/components/errors/error-page";
import { logError } from "~/shared/services/error-reporting";
import { toast } from "~/utils/toast";
import { createSession, type ISessionContext } from "./session-context";
import { useAuthToken, useAuthUser, useOrganisation, useUser } from "./hooks";

export type IUserSettings = Pick<IUser, "settings">["settings"];

export interface IAuthContext {
  loading: boolean;
  isLoggedIn: boolean;
  session: ISessionContext | null;
  error?: Error;
  authToken?: string;
  sendLoginLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (profile: UserProfile) => Promise<void>;
  updateUserSettings: (settings: Partial<IUserSettings>) => Promise<void>;
  signInWithOauth: (type: OAuthProviderType) => Promise<UserCredential>;
}

export const AuthContext = createContext<IAuthContext>(undefined as never);

export const useAuth = () => use(AuthContext);

const OAUTH_PROVIDERS = {
  google: new GoogleAuthProvider(),
};

type OAuthProviderType = keyof typeof OAUTH_PROVIDERS;

export interface UserProfile {
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation(["login", "error", "general"]);
  const [error, setError] = useState<Error>();

  const authUser = useAuthUser();
  const user = useUser(authUser.loading ? undefined : authUser.data?.uid);
  const organisation = useOrganisation(user.data?.organisation?.id);
  const authToken = useAuthToken(authUser.data);

  const signOut = useCallback(() => auth.signOut(), []);

  const updateUserSettings = useCallback(
    async (settings: Partial<IUserSettings>) => {
      try {
        if (!authUser.data?.uid) {
          return;
        }

        const userRef = doc(getUsersCollection(), authUser.data.uid);
        await setDoc(userRef, { settings }, { merge: true });
      } catch (err) {
        logError(err);
        setError(err as Error);
      }
    },
    [authUser.data],
  );

  const updateUserProfile = useCallback(
    async (profile: UserProfile) => {
      if (!authUser.data) {
        return;
      }

      try {
        const userRef = doc(getUsersCollection(), authUser.data.uid);

        await updateProfile(authUser.data, {
          displayName: `${profile.firstName} ${profile.lastName}`,
          photoURL: profile.photoUrl,
        });

        await updateDoc(userRef, {
          givenName: profile.firstName,
          familyName: profile.lastName,
        });
      } catch (err) {
        setError(err as Error);
        logError(err);
      }
    },
    [authUser.data],
  );

  const signInWithOauth = useCallback(async (type: OAuthProviderType) => {
    const provider = OAUTH_PROVIDERS[type];

    if (!provider) {
      throw new Error(`No provider found for type ${type}`);
    }

    return signInWithPopup(auth, provider);
  }, []);

  const sendLoginLink = useCallback(async (email: string) => {
    try {
      window.localStorage.setItem("emailForSignIn", email);

      return await sendSignInLinkToEmail(auth, email, {
        handleCodeInApp: true,
        url: `${window.location.origin}/login-link`,
      });
    } catch (e) {
      window.localStorage.removeItem("emailForSignIn");
      throw e;
    }
  }, []);

  const loading = authUser.loading || user.loading || organisation.loading;
  const isLoggedIn = !authUser.loading && Boolean(authUser.data);
  // A Firestore listener error ends the subscription, so undefined data here does not mean the document is missing.
  const loadError = user.error ?? organisation.error;

  useEffect(() => {
    if (loading || !isLoggedIn) {
      return;
    }

    if (loadError) {
      logError(loadError);
      return;
    }

    if (!user.data) {
      logError(`User not found for id: ${authUser.data?.uid}`);
      toast.error(t("login:error.loginFailed"));
      signOut().then();
      return;
    }

    if (!organisation.data || organisation.data.deletedAt) {
      logError(
        `No active organisation for userId: ${user.data.id}, organisationId: ${user.data.organisation?.id}`,
      );
      toast.error(t("login:error.noOrganisation"));
      signOut().then();
    }
  }, [
    loading,
    isLoggedIn,
    loadError,
    authUser.data,
    user.data,
    organisation.data,
    signOut,
    t,
  ]);

  const session = useMemo(
    () => createSession(authUser.data, user.data, organisation.data),
    [authUser.data, user.data, organisation.data],
  );

  const value = useMemo(
    () => ({
      loading,
      session,
      error,
      authToken,
      isLoggedIn,
      sendLoginLink,
      signOut,
      updateUserProfile,
      updateUserSettings,
      signInWithOauth,
    }),
    [
      loading,
      session,
      error,
      authToken,
      isLoggedIn,
      sendLoginLink,
      signOut,
      updateUserProfile,
      updateUserSettings,
      signInWithOauth,
    ],
  );

  if (loading) {
    return <GlobalLoading label={t("general:globalLoading.account")} />;
  }

  if (isLoggedIn && loadError) {
    return (
      <ErrorPage
        title={t("error:fallback.title")}
        message={t("error:fallback.message")}
        error={loadError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return <AuthContext value={value}>{children}</AuthContext>;
};
